import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileText, 
  MapPin, 
  Search, 
  Home, 
  CheckCircle2, 
  Users,
  Shield,
  Clock,
  X
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG = "https://images.unsplash.com/photo-1769366316790-dfcb6a546f05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxjb3p5JTIwbW9kZXJuJTIwbGl2aW5nJTIwcm9vbSUyMGV2ZW5pbmclMjBpbnRlcmlvcnxlbnwwfHx8fDE3Njk4NzgxNTd8MA&ixlib=rb-4.1.0&q=85";
const AGENT_PHOTO = "https://customer-assets.emergentagent.com/job_8045b904-4743-476b-a3b1-f22a821827d1/artifacts/565xb0in_Untitled%20image.jpg";

const checklistSections = [
  {
    icon: FileText,
    title: "The Foundation",
    description: "Budget & document checklist to get you rental-ready",
    color: "#E07A5F"
  },
  {
    icon: MapPin,
    title: "The Search",
    description: "Neighborhood scoring matrix & 10 must-ask questions",
    color: "#84A98C"
  },
  {
    icon: Search,
    title: "The Inspection",
    description: "Room-by-room walkthrough for professional-level reviews",
    color: "#E07A5F"
  },
  {
    icon: Home,
    title: "The Sanctuary",
    description: "Move-in essentials & wellness setup guide",
    color: "#84A98C"
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadCount, setDownloadCount] = useState(247);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasShownPopup, setHasShownPopup] = useState(false);

  useEffect(() => {
    // Fetch download count
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/download-count`);
        setDownloadCount(res.data.count);
      } catch (e) {
        console.error("Error fetching count:", e);
      }
    };
    fetchCount();
  }, []);

  // Exit intent detection
  const handleMouseLeave = useCallback((e) => {
    if (e.clientY <= 0 && !hasShownPopup && !showExitPopup) {
      setShowExitPopup(true);
      setHasShownPopup(true);
    }
  }, [hasShownPopup, showExitPopup]);

  useEffect(() => {
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [handleMouseLeave]);

  const handleSubmit = async (e, fromPopup = false) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const res = await axios.post(`${API}/leads`, { name, email });
      toast.success("Success! Check your email for the checklist.");
      
      // Store lead ID for thank you page
      sessionStorage.setItem("leadId", res.data.id);
      sessionStorage.setItem("leadName", name);
      
      if (fromPopup) {
        setShowExitPopup(false);
      }
      
      navigate("/thank-you");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Hero Section */}
      <section 
        className="hero-bg min-h-screen flex items-center relative"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      >
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="space-y-8">
              {/* Social proof badge */}
              <div 
                data-testid="social-proof-badge"
                className="inline-flex items-center gap-2 glass-card px-4 py-2 animate-fadeInUp"
              >
                <Users className="w-4 h-4 text-[#84A98C]" />
                <span className="text-sm text-[#F1F5F9]">
                  Join <span className="font-semibold text-[#E07A5F] counter-number">{downloadCount.toLocaleString()}</span> savvy renters
                </span>
              </div>
              
              <h1 
                data-testid="hero-headline"
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight animate-fadeInUp delay-100"
              >
                Stop Overlooking These{" "}
                <span className="text-[#E07A5F]">23 Things</span>{" "}
                on Apartment Tours
              </h1>
              
              <p 
                data-testid="hero-subheadline"
                className="text-lg text-[#F1F5F9]/80 max-w-lg animate-fadeInUp delay-200"
              >
                Get my free Sanctuary Seeker's Checklist — the same guide I give all my clients to find their perfect apartment.
              </p>
              
              {/* Trust indicators */}
              <div className="flex flex-wrap gap-4 animate-fadeInUp delay-300">
                <div className="flex items-center gap-2 text-sm text-[#84A98C]">
                  <Shield className="w-4 h-4" />
                  <span>No spam. Ever.</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#84A98C]">
                  <Clock className="w-4 h-4" />
                  <span>Instant access</span>
                </div>
              </div>
            </div>
            
            {/* Right content - Form */}
            <div className="animate-fadeInUp delay-400">
              <div className="glass-card p-8 max-w-md mx-auto lg:ml-auto">
                <h2 className="text-2xl font-semibold text-white mb-2">
                  Get Your Free Checklist
                </h2>
                <p className="text-[#F1F5F9]/70 mb-6 text-sm">
                  Sent straight to your inbox — no strings attached
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      data-testid="name-input"
                      type="text"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="input-field bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-[#E07A5F] focus:border-[#E07A5F]"
                    />
                  </div>
                  <div>
                    <Input
                      data-testid="email-input"
                      type="email"
                      placeholder="Your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-[#E07A5F] focus:border-[#E07A5F]"
                    />
                  </div>
                  <Button
                    data-testid="submit-button"
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary bg-[#E07A5F] hover:bg-[#C66A50] text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-transform hover:-translate-y-1"
                  >
                    {isSubmitting ? "Sending..." : "Send Me the Checklist"}
                  </Button>
                </form>
                
                <p className="text-xs text-[#F1F5F9]/50 mt-4 text-center">
                  Your privacy is respected. Unsubscribe anytime.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2" />
          </div>
        </div>
      </section>
      
      {/* What You'll Get Section */}
      <section className="py-20 md:py-32 bg-[#1E3A5F] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              data-testid="checklist-section-title"
              className="text-3xl sm:text-4xl font-bold text-white mb-4"
            >
              What's Inside Your Checklist
            </h2>
            <p className="text-[#F1F5F9]/70 text-lg max-w-2xl mx-auto">
              Four comprehensive sections covering everything from budget planning to move-in day
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {checklistSections.map((section, index) => (
              <div
                key={section.title}
                data-testid={`checklist-card-${index}`}
                className="glass-card p-8 hover:bg-white/10 transition-colors group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${section.color}20` }}
                >
                  <section.icon className="w-7 h-7" style={{ color: section.color }} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {section.title}
                </h3>
                <p className="text-[#F1F5F9]/70 text-sm leading-relaxed">
                  {section.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* About Bryan Section */}
      <section className="py-20 md:py-32 bg-[#0F172A] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Photo */}
            <div className="relative">
              <div className="relative z-10 max-w-md mx-auto lg:mx-0">
                <img
                  data-testid="agent-photo"
                  src={AGENT_PHOTO}
                  alt="Bryan - Your Apartment Locator"
                  className="w-full aspect-square object-cover rounded-2xl shadow-2xl"
                />
                {/* Decorative elements */}
                <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#84A98C] rounded-2xl -z-10" />
              </div>
            </div>
            
            {/* Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 glass-card px-4 py-2">
                <CheckCircle2 className="w-4 h-4 text-[#84A98C]" />
                <span className="text-sm text-[#84A98C]">Licensed Apartment Locator</span>
              </div>
              
              <h2 
                data-testid="about-section-title"
                className="text-3xl sm:text-4xl font-bold text-white"
              >
                Hi, I'm Bryan
              </h2>
              
              <p className="text-[#F1F5F9]/80 text-lg leading-relaxed">
                I've helped hundreds of renters find their perfect apartment — and avoid costly mistakes along the way. This checklist is the exact tool I give to all my clients.
              </p>
              
              <p className="text-[#F1F5F9]/70 leading-relaxed">
                Whether you're a first-time renter or relocating to a new city, having a systematic approach makes all the difference. I created Urban Nest Quest to help renters feel confident and prepared throughout their apartment search.
              </p>
              
              <div className="pt-4">
                <a
                  href="#get-checklist"
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="btn-primary inline-block"
                  data-testid="about-cta-button"
                >
                  Get Your Free Checklist
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Google Form Section - Detailed Inquiry */}
      <section className="py-20 md:py-32 bg-[#0F172A] relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              data-testid="google-form-title"
              className="text-3xl sm:text-4xl font-bold text-white mb-4"
            >
              Tell Us About Your Apartment Search
            </h2>
            <p className="text-[#F1F5F9]/70 text-lg max-w-2xl mx-auto">
              Fill out this quick form so we can better understand your needs and help you find your perfect home.
            </p>
          </div>
          
          <div className="glass-card p-4 sm:p-8 overflow-hidden">
            <div className="relative w-full" style={{ paddingBottom: '125%', minHeight: '600px' }}>
              <iframe
                data-testid="google-form-iframe"
                src="https://docs.google.com/forms/d/e/1FAIpQLSdVCQXj5dS1ntnXo__6AMfztab7SLnUkH2a8SVarVm2h_7NTw/viewform?embedded=true"
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                style={{ border: 'none', background: 'white' }}
                title="Urban Nest Quest Inquiry Form"
                loading="lazy"
              >
                Loading…
              </iframe>
            </div>
          </div>
        </div>
      </section>
      
      {/* Final CTA Section */}
      <section className="py-20 md:py-32 bg-[#1E3A5F] relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            data-testid="final-cta-title"
            className="text-3xl sm:text-4xl font-bold text-white mb-6"
          >
            Ready to Find Your Perfect Apartment?
          </h2>
          <p className="text-[#F1F5F9]/80 text-lg mb-8">
            Download your free Sanctuary Seeker's Checklist and never miss a red flag again.
          </p>
          
          <form 
            onSubmit={handleSubmit} 
            className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
          >
            <Input
              data-testid="footer-email-input"
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
            />
            <Button
              data-testid="footer-submit-button"
              type="submit"
              disabled={isSubmitting}
              className="btn-primary bg-[#E07A5F] hover:bg-[#C66A50] whitespace-nowrap rounded-full px-8"
            >
              {isSubmitting ? "..." : "Get Checklist"}
            </Button>
          </form>
          
          <p className="text-xs text-[#F1F5F9]/50 mt-6">
            No spam. Just value. Your privacy is respected.
          </p>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 bg-[#0F172A] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#F1F5F9]/50 text-sm">
              © {new Date().getFullYear()} Urban Nest Quest. All rights reserved.
            </p>
            <a 
              href="/admin" 
              className="text-[#F1F5F9]/30 text-xs hover:text-[#F1F5F9]/50 transition-colors"
            >
              Admin
            </a>
          </div>
        </div>
      </footer>
      
      {/* Exit Intent Popup */}
      {showExitPopup && (
        <div 
          data-testid="exit-popup"
          className="popup-overlay"
        >
          <div className="glass-card p-8 max-w-md mx-4 relative animate-fadeInUp bg-[#1E3A5F]">
            <button
              data-testid="close-popup-button"
              onClick={() => setShowExitPopup(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <h3 className="text-2xl font-bold text-white mb-2">
              Wait! Don't Miss Out
            </h3>
            <p className="text-[#F1F5F9]/70 mb-6">
              Get your free Renter's Checklist before you go — it'll save you hours of headaches.
            </p>
            
            <form onSubmit={(e) => handleSubmit(e, true)} className="space-y-4">
              <Input
                data-testid="popup-name-input"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
              <Input
                data-testid="popup-email-input"
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
              <Button
                data-testid="popup-submit-button"
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary bg-[#E07A5F] hover:bg-[#C66A50] rounded-full py-6"
              >
                {isSubmitting ? "Sending..." : "Get Instant Access"}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
